/**
 * Integrytas Group - Base App (MOBILE)
 * Page: PFW.PORTALS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/PfwPortalManagerPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + status pills + bottom-sheet detail
 * + edit sheet) over the SAME data/logic layer only:
 *   - reads via apiDb (pfw_portal, plus roll-up counts from pfw_portal_view /
 *     pfw_portal_entity / pfw_api_key so the detail sheet can summarise a portal's
 *     curated views, entity whitelist and active API keys — scannable on a phone),
 *   - the activate/deactivate toggle and the scalar edits (name, branding,
 *     allow-external, active) route through the Common Routine (routedUpdate) — §2
 *     single write path; never a direct apiDb write.
 * It never re-renders the desktop grid; §16. The desktop still owns creating
 * portals and editing curated views / entity whitelist / API keys — those are shown
 * read-only here as counts (mirrors MobileUserEditSheet's read-only roles).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, SquarePen, Globe } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'PFW.PORTALS';

interface MobilePortalRow {
  pfw_portal_id: number;
  internal_code: string;
  name: string;
  branding: Record<string, unknown> | null;
  allow_external: 'Y' | 'N';
  is_active: 'Y' | 'N';
  description: string | null;
  views: number;
  entities: number;
  active_keys: number;
}

type StatusFacet = 'all' | 'active' | 'inactive';

// ---------------------------------------------------------------------------
// List screen
// ---------------------------------------------------------------------------

export function MobilePfwPortalManagerPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [rows, setRows] = useState<MobilePortalRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [externalOnly, setExternalOnly] = useState(false);
  const [selected, setSelected] = useState<MobilePortalRow | null>(null);
  const [editing, setEditing] = useState<MobilePortalRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data: rawPortals, error: pErr } = await apiDb
        .from('pfw_portal')
        .select('pfw_portal_id, internal_code, name, branding, allow_external, is_active, description')
        .order('internal_code');
      if (pErr) throw pErr;

      // Roll up each portal's child collections into scannable counts (mirrors the
      // desktop's per-portal Curated Views / Entity Whitelist / API Keys sections).
      const [vRes, eRes, kRes] = await Promise.all([
        apiDb.from('pfw_portal_view').select('pfw_portal_id'),
        apiDb.from('pfw_portal_entity').select('pfw_portal_id'),
        apiDb.from('pfw_api_key').select('pfw_portal_id, is_active'),
      ]);
      if (vRes.error) throw vRes.error;
      if (eRes.error) throw eRes.error;
      if (kRes.error) throw kRes.error;

      const viewCounts: Record<number, number> = {};
      for (const v of (vRes.data ?? []) as { pfw_portal_id: number }[]) {
        viewCounts[v.pfw_portal_id] = (viewCounts[v.pfw_portal_id] ?? 0) + 1;
      }
      const entityCounts: Record<number, number> = {};
      for (const en of (eRes.data ?? []) as { pfw_portal_id: number }[]) {
        entityCounts[en.pfw_portal_id] = (entityCounts[en.pfw_portal_id] ?? 0) + 1;
      }
      const keyCounts: Record<number, number> = {};
      for (const k of (kRes.data ?? []) as { pfw_portal_id: number; is_active: 'Y' | 'N' }[]) {
        if (k.is_active === 'Y') keyCounts[k.pfw_portal_id] = (keyCounts[k.pfw_portal_id] ?? 0) + 1;
      }

      type RawPortal = Omit<MobilePortalRow, 'views' | 'entities' | 'active_keys' | 'branding'> & { branding: unknown };
      const list: MobilePortalRow[] = ((rawPortals ?? []) as RawPortal[]).map((p) => ({
        pfw_portal_id: p.pfw_portal_id,
        internal_code: p.internal_code,
        name: p.name,
        branding: (p.branding && typeof p.branding === 'object' ? (p.branding as Record<string, unknown>) : null),
        allow_external: p.allow_external,
        is_active: p.is_active,
        description: p.description,
        views: viewCounts[p.pfw_portal_id] ?? 0,
        entities: entityCounts[p.pfw_portal_id] ?? 0,
        active_keys: keyCounts[p.pfw_portal_id] ?? 0,
      }));
      setRows(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Load once on mount; portal labels are plain columns, not locale-bound _tl rows.
  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (externalOnly && r.allow_external !== 'Y') return false;
      if (!q) return true;
      return r.internal_code.toLowerCase().includes(q) || (r.name ?? '').toLowerCase().includes(q);
    });
  }, [rows, filter, statusFacet, externalOnly]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.pfw.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const titleOf = (r: MobilePortalRow) => (r.name && r.name.trim() ? r.name : r.internal_code);
  const countsLabel = (r: MobilePortalRow) =>
    `${t('mobile.pfw.views_count', { count: r.views, defaultValue: `${r.views} views` })} · ` +
    `${t('mobile.pfw.keys_count', { count: r.active_keys, defaultValue: `${r.active_keys} keys` })}`;

  const toggleActive = async (r: MobilePortalRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'pfw_portal', 'pfw_portal_id', r.pfw_portal_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.pfw.title', 'Portals')}
        description={t('mobile.pfw.description', 'External and internal portals, their curated views and API keys.')}
      />

      <MobileStatStrip
        stats={[
          { value: rows.length, label: t('mobile.pfw.stat.total', 'Total') },
          { value: rows.filter((r) => r.is_active === 'Y').length, label: t('common.boolean.active', 'Active') },
          { value: rows.filter((r) => r.allow_external === 'Y').length, label: t('mobile.pfw.stat.external', 'External') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.pfw.search', 'Search portals')}
          aria-label={t('mobile.pfw.search', 'Search portals')}
        />
      </div>

      <MobileFilterChips
        chips={[
          ...statusFacets.map((f) => ({ value: f.key, label: f.label })),
          { value: '__external', label: t('mobile.pfw.external_only', 'External') },
        ]}
        isActive={(v) => (v === '__external' ? externalOnly : statusFacet === v)}
        onSelect={(v) => (v === '__external' ? setExternalOnly((p) => !p) : setStatusFacet(v as StatusFacet))}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.pfw.none', 'No portals match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.pfw_portal_id}
              lead={<Globe />}
              leadTone={r.allow_external === 'Y' ? 'blue' : 'slate'}
              title={titleOf(r)}
              subtitle={`${r.internal_code} · ${countsLabel(r)}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.pfw.detail', 'Portal detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Globe size={20} /></span>
              <span>
                <div className="m-nm">{titleOf(selected)}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.pfw.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.pfw.allow_external', 'External access')}
                value={
                  <MobileStatusPill tone={selected.allow_external === 'Y' ? 'ok' : 'no'}>
                    {selected.allow_external === 'Y' ? t('common.yes', 'Yes') : t('common.no', 'No')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.pfw.curated_views', 'Curated views')} value={selected.views} />
              <MobileDetailRow label={t('mobile.pfw.entity_whitelist', 'Whitelisted entities')} value={selected.entities} />
              <MobileDetailRow label={t('mobile.pfw.api_keys', 'Active API keys')} value={selected.active_keys} />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.pfw.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobilePortalEditSheet
        session={session}
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the portal's scalar fields (name, branding title/logo, allow
// external, active), saved through the Common Routine (routedUpdate) — §2. The
// curated views / entity whitelist / API keys stay on the desktop, so they are
// summarised read-only in the detail sheet above rather than edited here.
// ---------------------------------------------------------------------------

interface MobilePortalEditSheetProps {
  session: AuthSession;
  row: MobilePortalRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function brandingString(branding: Record<string, unknown> | null, key: string): string {
  const v = branding?.[key];
  return typeof v === 'string' ? v : '';
}

function MobilePortalEditSheet({ session, row, onClose, onSaved }: MobilePortalEditSheetProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [brandTitle, setBrandTitle] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [allowExternal, setAllowExternal] = useState(false);
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different portal is opened.
  useEffect(() => {
    if (!row) return;
    setName(row.name ?? '');
    setBrandTitle(brandingString(row.branding, 'title'));
    setLogoUrl(brandingString(row.branding, 'logo_url'));
    setAllowExternal(row.allow_external === 'Y');
    setActive(row.is_active === 'Y');
    setError(null);
  }, [row]);

  const save = async () => {
    if (!row) return;
    setBusy(true);
    setError(null);
    // Preserve any branding keys the desktop set beyond title/logo_url (immutable
    // merge — never mutate the original object).
    const branding: Record<string, unknown> = {
      ...(row.branding ?? {}),
      title: brandTitle.trim(),
      logo_url: logoUrl.trim(),
    };
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'pfw_portal', 'pfw_portal_id', row.pfw_portal_id, {
      name: name.trim(),
      branding,
      allow_external: allowExternal ? 'Y' : 'N',
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.pfw.edit_sheet', 'Edit portal')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Globe size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.pfw.edit_title', 'Edit portal')}</div>
                <div className="m-em">{row.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label>{t('mobile.pfw.code', 'Code')}</label>
                <div className="m-formrow-ro">
                  {row.internal_code}
                  <span className="m-formrow-hint"> · {t('mobile.pfw.code_locked', 'cannot be changed')}</span>
                </div>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-pfw-name">{t('mobile.pfw.name', 'Name')}</label>
                <input
                  id="m-pfw-name"
                  className="m-inp"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-pfw-brand-title">{t('mobile.pfw.brand_title', 'Branding title')}</label>
                <input
                  id="m-pfw-brand-title"
                  className="m-inp"
                  value={brandTitle}
                  onChange={(e) => setBrandTitle(e.target.value)}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-pfw-logo">{t('mobile.pfw.logo_url', 'Logo URL')}</label>
                <input
                  id="m-pfw-logo"
                  className="m-inp"
                  type="url"
                  inputMode="url"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.pfw.allow_external', 'External access')}</div>
                  <div className="m-formrow-s">{t('mobile.pfw.allow_external_hint', 'Portal is reachable outside the app')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={allowExternal}
                  className={'m-switch ' + (allowExternal ? 'm-on' : 'm-off')}
                  onClick={() => setAllowExternal((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.pfw.active_hint', 'Portal is enabled')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
